/**
 * parkingApi — thin wrapper over the Supabase Edge Functions that power
 * RGV Smart Pay (permits, validation, enforcement, vendor onboarding).
 *
 * Every lot is addressed by its `slug` (e.g. "spacemonkey"), so onboarding a
 * new vendor requires no front-end code changes.
 */
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';

async function invoke(fnName, body) {
  if (!isSupabaseConfigured) {
    throw new Error(
      'Backend not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.'
    );
  }

  const { data, error } = await supabase.functions.invoke(fnName, { body });

  if (error) {
    // Edge Functions return a JSON { error } body on failure; surface it.
    let message = error.message || 'Request failed';
    try {
      const ctx = await error.context?.json();
      if (ctx?.error) message = ctx.error;
      if (ctx?.reason) message = ctx.reason;
    } catch {
      /* keep default message */
    }
    const e = new Error(message);
    e.data = error;
    throw e;
  }

  return data;
}

const parkingApi = {
  /** Create a Stripe PaymentIntent + pending permit. Returns { clientSecret, permit_number, ... }. */
  createPermitIntent: ({ slug, licensePlate, state, email }) =>
    invoke('create-permit-intent', {
      slug,
      license_plate: licensePlate,
      state,
      email,
    }),

  /** Mark a permit active immediately after Stripe confirms the card payment. */
  confirmPermit: (paymentIntentId) =>
    invoke('confirm-permit', { payment_intent_id: paymentIntentId }),

  /** Redeem a customer validation code. Returns { valid, plate, reason? }. */
  validateCode: ({ slug, code, licensePlate }) =>
    invoke('validate-code', { slug, code, license_plate: licensePlate }),

  /** Look up a plate's enforcement status. Returns { status, plate, ... }. */
  lookupPlate: ({ slug, plate }) =>
    invoke('enforcement-lookup', { slug, plate }),

  /** Live dashboard figures for a lot. */
  lotSummary: (slug) => invoke('lot-summary', { slug }),

  /** Onboard a new vendor (business + first lot). */
  vendorSignup: (payload) => invoke('vendor-signup', payload),
};

export default parkingApi;
export { isSupabaseConfigured };
